import React, { useEffect, useMemo, useState } from 'react';
import PayInService from '../../services/PayInService';
import BranchService from '../../services/BranchService';
import StaffService from '../../services/StaffService';
import StakeholderService from '../../services/StakeholderService';
import ClientService from '../../services/ClientService';
import ProjectService from '../../services/ProjectService';
import utility from '../../utils/utility';
import ReactPaginate from 'react-paginate';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const PayIn = () => {
    const [payIns, setPayIns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [searchText, setSearchText] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [filterBranch, setFilterBranch] = useState('');
    const [filterProject, setFilterProject] = useState('');
    const [filterClient, setFilterClient] = useState('');
    const [filterIncomeType, setFilterIncomeType] = useState('');
    const [filterPaymentMode, setFilterPaymentMode] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [formData, setFormData] = useState({
        id: '',
        branchId: '',
        entityType: 'stakeholder', // staff, stakeholder, client
        staffId: '',
        stakeholderId: '',
        clientId: '',
        projectId: '',
        paymentDate: '',
        incomeType: '',
        amount: '',
        paymentMode: 'Cash',
        transactionNo: '',
        remarks: '',
        transactionImage: '',
        status: 'completed',
        isActive: 1
    });
    const filteredPayIns = useMemo(() => payIns.filter((payIn) => {
        if (!searchText) return true;
        return filterList([payIn], searchText, ['paymentMode', 'transactionNo', 'projectName', 'incomeType', 'branchName', 'status', 'staffName', 'stakeholderName', 'vendorName']).length > 0;
    }), [payIns, searchText]);

    const handleFilterChange = (setter) => (event) => {
        setter(event.target.value);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSearchText('');
        setFilterDateFrom('');
        setFilterDateTo('');
        setFilterBranch('');
        setFilterProject('');
        setFilterClient('');
        setFilterPaymentMode('');
        setFilterIncomeType('');
        setFilterStatus('');
        setCurrentPage(1);
    };

    useEffect(() => {
        fetchDropdownData();
    }, []);

    useEffect(() => {
        fetchPayIns();
    }, [currentPage, filterDateFrom, filterDateTo, filterBranch, filterProject, filterClient, filterPaymentMode, filterStatus]);

    // Dropdown data
    const [branches, setBranches] = useState([]);
    const [staffs, setStaffs] = useState([]);
    const [stakeholders, setStakeholders] = useState([]);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        fetchPayIns();
        fetchDropdownData();
    }, [currentPage]);

    const fetchPayIns = async () => {
        try {
            setLoading(true);
            const data = await PayInService.getAllPayIn(currentPage, itemsPerPage, {
                branchId: filterBranch,
                projectId: filterProject,
                clientId: filterClient,
                incomeType: filterIncomeType,
                paymentMode: filterPaymentMode,
                incomeType: filterIncomeType,
                status: filterStatus,
                startDate: filterDateFrom,
                endDate: filterDateTo
            });
            setPayIns(data.data || []);
            setPageCount(data.pagination?.totalPages || 0);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDropdownData = async () => {
        try {
            const [branchRes, staffRes, stakeholderRes, clientRes, projectRes] = await Promise.all([
                BranchService.getAllBranches(1, 100),
                StaffService.getAll(1, 100),
                StakeholderService.getAll(1, 100),
                ClientService.getAll(1, 100),
                ProjectService.getAll(1, 100)
            ]);
            setBranches(branchRes.data || []);
            setStaffs(staffRes.data || []);
            setStakeholders(stakeholderRes.data || []);
            setClients(clientRes.data || []);
            setProjects(projectRes.data || []);
        } catch (err) {
            console.error('Failed to fetch dropdown data', err);
        }
    };

    const handleOpenModal = (payIn = null) => {
        if (payIn) {
            setEditMode(true);
            let entityType = 'staff';
            if (payIn.stakeholderId) entityType = 'stakeholder';
            else if (payIn.clientId) entityType = 'client';

            setFormData({
                id: payIn.id,
                branchId: payIn.branchId || '',
                entityType: entityType,
                staffId: payIn.staffId || '',
                stakeholderId: payIn.stakeholderId || '',
                clientId: payIn.clientId || '',
                incomeType: payIn.incomeType || '',
                projectId: payIn.projectId || '',
                paymentDate: payIn.paymentDate ? payIn.paymentDate.split('T')[0] : '',
                amount: payIn.amount || '',
                paymentMode: payIn.paymentMode || 'Cash',
                transactionNo: payIn.transactionNo || '',
                remarks: payIn.remarks || '',
                transactionImage: payIn.transactionImage || '',
                status: payIn.status || 'completed',
                isActive: payIn.isActive
            });
        } else {
            setEditMode(false);
            setFormData({
                id: '',
                branchId: '',
                entityType: 'staff',
                staffId: '',
                stakeholderId: '',
                clientId: '',
                projectId: '',
                paymentDate: new Date().toISOString().split('T')[0],
                amount: '',
                paymentMode: 'Cash',
                transactionNo: '',
                remarks: '',
                transactionImage: '',
                status: 'completed',
                isActive: 1
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'isActive' ? parseInt(value) : value,
            // Reset other entity IDs when entityType changes
            ...(name === 'entityType' ? { staffId: '', stakeholderId: '', clientId: '' } : {})
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            // Prepare payload: only include the selected entity ID
            const payload = { ...formData };
            if (formData.entityType === 'staff') { payload.stakeholderId = null; payload.clientId = null; }
            else if (formData.entityType === 'stakeholder') { payload.staffId = null; payload.clientId = null; }
            else if (formData.entityType === 'client') { payload.staffId = null; payload.stakeholderId = null; }

            if (editMode) {
                await PayInService.updatePayIn(formData.id, payload);
                utility.showSuccess('Pay-in transaction updated successfully');
            } else {
                await PayInService.createPayIn(payload);
                utility.showSuccess('Pay-in transaction created successfully');
            }
            handleCloseModal();
            fetchPayIns();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (payIn) => {
        try {
            const newStatus = payIn.isActive ? 0 : 1;
            await PayInService.updateStatus(payIn.id, newStatus);
            utility.showSuccess(`Pay-in status ${newStatus ? 'activated' : 'deactivated'} successfully`);
            fetchPayIns();
        } catch (err) {
            utility.showError(err.message);
        }
    };

    const handlePageClick = (event) => {
        setCurrentPage(event.selected + 1);
    };

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 text-end mb-3">
                    <button className="btn bg-gradient-dark mb-0" onClick={() => handleOpenModal()}>
                        <i className="material-symbols-rounded text-sm">add</i>&nbsp;&nbsp;Add New Pay-in
                    </button>
                </div>
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Pay In Transactions</h6>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-2">
                            <div className="px-3 mb-3">
                                <div className="bg-white rounded-3 shadow-sm p-3">
                                    <div className="row gy-2 gx-2 align-items-end">
                                        <div className="col-lg-3 col-md-6">
                                            <FilterBar
                                                value={searchText}
                                                onChange={setSearchText}
                                                onClear={() => setSearchText('')}
                                                placeholder="Search pay-ins..."
                                            />
                                        </div>
                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">Date From</label>
                                            <input type="date" className="form-control" value={filterDateFrom} onChange={handleFilterChange(setFilterDateFrom)} />
                                        </div>
                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">Date To</label>
                                            <input type="date" className="form-control" value={filterDateTo} onChange={handleFilterChange(setFilterDateTo)} />
                                        </div>
                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">Branch</label>
                                            <select className="form-select" value={filterBranch} onChange={handleFilterChange(setFilterBranch)}>
                                                <option value="">All Branches</option>
                                                {branches.map((branch) => (
                                                    <option key={branch.id} value={branch.id}>{branch.branchName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Project</label>
                                            <select className="form-select" value={filterProject} onChange={handleFilterChange(setFilterProject)}>
                                                <option value="">All Projects</option>
                                                {projects.map((project) => (
                                                    <option key={project.id} value={project.id}>{project.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Client</label>
                                            <select className="form-select" value={filterClient} onChange={handleFilterChange(setFilterClient)}>
                                                <option value="">All Clients</option>
                                                {clients.map((client) => (
                                                    <option key={client.id} value={client.id}>{client.clientName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-1">
                                            <label className="form-label text-muted small">Mode</label>
                                            <select className="form-select" value={filterPaymentMode} onChange={handleFilterChange(setFilterPaymentMode)}>
                                                <option value="">Any</option>
                                                <option value="Cash">Cash</option>
                                                <option value="UPI">UPI</option>
                                                <option value="NEFT">NEFT</option>
                                                <option value="RTGS">RTGS</option>
                                            </select>
                                        </div>
                                        <div className="col-md-1">
                                            <label className="form-label text-muted small">Status</label>
                                            <select className="form-select" value={filterStatus} onChange={handleFilterChange(setFilterStatus)}>
                                                <option value="">Any</option>
                                                <option value="completed">Completed</option>
                                                <option value="pending">Pending</option>
                                                <option value="cancelled">Cancelled</option>
                                            </select>
                                        </div>
                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">&nbsp;</label>
                                            <button type="button" className="mt-1 btn btn-outline-secondary w-100" onClick={resetFilters}>
                                                Clear Filters
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive p-0">
                                {loading ? (
                                    <div className="p-4 text-center">Loading...</div>
                                ) : (
                                    <table className="table table-bordered align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Date</th>


                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Entity / Project</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Income Type</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Mode</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-end">Amount (₹)</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Status</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredPayIns.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-4">No pay-in transactions found.</td>
                                                </tr>
                                            ) : (
                                                filteredPayIns.map((payIn) => (
                                                    <tr key={payIn.id}>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0 px-3">{payIn.paymentDate ? payIn.paymentDate.split('T')[0] : 'N/A'}</p>
                                                        </td>


                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">
                                                                {payIn.staffName || payIn.stakeholderName || payIn.clientName || 'N/A'}
                                                            </p>
                                                            <p className="text-xs text-secondary mb-0">{payIn.projectName || 'No Project'}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{payIn.incomeType.replace("_", " ") || 'N/A'}</p>


                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{payIn.paymentMode || 'N/A'}</p>
                                                            <p className="text-xs text-secondary mb-0">{payIn.transactionNo || ''}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0 text-end">₹{payIn.amount || '0.00'}</p>
                                                        </td>
                                                        <td>
                                                            <span className={`badge badge-sm bg-gradient-${payIn.isActive ? 'success' : 'secondary'} cursor-pointer`} onClick={() => handleToggleStatus(payIn)}>
                                                                {payIn.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <button className="btn btn-link text-info mb-0" onClick={() => handleOpenModal(payIn)}>
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="6">
                                                    <ReactPaginate
                                                        forcePage={currentPage - 1}
                                                        breakLabel="..."
                                                        nextLabel=">"
                                                        onPageChange={handlePageClick}
                                                        pageRangeDisplayed={3}
                                                        marginPagesDisplayed={2}
                                                        pageCount={pageCount}
                                                        previousLabel="<"
                                                        containerClassName="pagination px-3"
                                                        pageClassName="page-item"
                                                        pageLinkClassName="page-link"
                                                        previousClassName="page-item"
                                                        previousLinkClassName="page-link"
                                                        nextClassName="page-item"
                                                        nextLinkClassName="page-link"
                                                        activeClassName="active"
                                                        disabledClassName="disabled"
                                                        breakClassName="page-item"
                                                        breakLinkClassName="page-link"
                                                    />
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title font-weight-normal">{editMode ? 'Edit Pay-in' : 'Add New Pay-in'}</h5>
                                <button type="button" className="btn-close text-dark" onClick={handleCloseModal}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body row">
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Branch</label>
                                            <select name="branchId" className="form-control" value={formData.branchId} onChange={handleInputChange} required>
                                                <option value="">Select Branch</option>
                                                {branches.map(b => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Project</label>
                                            <select name="projectId" className="form-control" value={formData.projectId} onChange={handleInputChange}>
                                                <option value="">Select Project</option>
                                                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-3 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Income Type</label>
                                            <select name="incomeType" className="form-control" value={formData.incomeType} onChange={handleInputChange}>
                                                <option value="">Select Income Type</option>
                                                <option value="Investor_Capital">Investor Capital</option>
                                                <option value="Sales_Revenue">Sales Revenue</option>
                                                <option value="Commission">Commission</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-3 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Entity Type</label>
                                            <select name="entityType" className="form-control" value={formData.entityType} onChange={handleInputChange}>
                                                <option value="stakeholder">Stakeholder</option>
                                                <option value="client">Client</option>
                                                <option value="staff">Staff</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Select {formData.entityType.charAt(0).toUpperCase() + formData.entityType.slice(1)}</label>
                                            {formData.entityType === 'staff' && (
                                                <select name="staffId" className="form-control" value={formData.staffId} onChange={handleInputChange} required>
                                                    <option value="">Select Staff</option>
                                                    {staffs.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.staffCode})</option>)}
                                                </select>
                                            )}
                                            {formData.entityType === 'stakeholder' && (
                                                <select name="stakeholderId" className="form-control" value={formData.stakeholderId} onChange={handleInputChange} required>
                                                    <option value="">Select Stakeholder</option>
                                                    {stakeholders.map(m => <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>)}
                                                </select>
                                            )}
                                            {formData.entityType === 'client' && (
                                                <select name="clientId" className="form-control" value={formData.clientId} onChange={handleInputChange} required>
                                                    <option value="">Select Client</option>
                                                    {clients.map(c => <option key={c.id} value={c.id}>{c.clientName}</option>)}
                                                </select>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Payment Date</label>
                                            <input type="date" name="paymentDate" className="form-control" value={formData.paymentDate} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Amount (₹)</label>
                                            <input type="number" name="amount" className="form-control" value={formData.amount} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Payment Mode</label>
                                            <select name="paymentMode" className="form-control" value={formData.paymentMode} onChange={handleInputChange}>
                                                <option value="Cash">Cash</option>
                                                <option value="Bank Transfer">Bank Transfer</option>
                                                <option value="UPI">UPI</option>
                                                <option value="Cheque">Cheque</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Transaction No</label>
                                            <input type="text" name="transactionNo" className="form-control" value={formData.transactionNo} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Remarks</label>
                                            <input type="text" name="remarks" className="form-control" value={formData.remarks} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Is Active</label>
                                            <select name="isActive" className="form-control" value={formData.isActive} onChange={handleInputChange}>
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button type="button" className="btn bg-gradient-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                                    <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                        {saving ? 'Saving...' : (editMode ? 'Update' : 'Save')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PayIn;

